import React, { useState, useEffect } from "react";
import { useFeatureFlags } from "../contexts/FeatureFlagContext";
import { FaHome, FaPlusCircle, FaListUl, FaUserCircle, FaFutbol, FaSignOutAlt, FaDollarSign, FaArrowRight, FaBell, FaUsers, FaUserFriends, FaCalendarCheck, FaCog, FaSearch } from "react-icons/fa";
import { MdSportsSoccer, MdDashboard, MdManageAccounts, MdAnalytics, MdBusiness, MdAdminPanelSettings } from "react-icons/md";
import { IconType } from "react-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { theme } from "../theme";
import { useResponsive } from "../hooks/useResponsive";
import { useAuth } from "../contexts/AuthContext";
import { NotificationButton } from "./NotificationButton";
import { NotificationsPanel } from "./NotificationsPanel";
import { Notification } from "../types";
import { getApiUrl } from "../config";
import { FeedbackSidebarButton } from "./FeedbackSidebarButton";
import { useSocialBadges } from "../hooks/useSocialBadges";
import { isAdminUser } from '../utils/roles';

// Componente para mostrar solo el contador de notificaciones
const NotificationCounter: React.FC<{ onNotificationClick: (notification: Notification) => void }> = ({ onNotificationClick }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  // Actualizar contador cuando se cierre el panel de notificaciones
  useEffect(() => {
    if (!showPanel && user?.id) {
      // Pequeño delay para asegurar que las notificaciones se marcaron como leídas
      setTimeout(() => {
        fetchUnreadCount();
      }, 500);
    }
  }, [showPanel, user?.id]);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(getApiUrl('/notifications/unread-count'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count || 0);
      }
    } catch {
      // silently ignore network errors for badge count
    }
  };

  const handleClick = () => {
    setShowPanel(true);
  };

  const handleClose = () => {
    setShowPanel(false);
  };

  const handleNotificationClick = (notification: Notification) => {
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
    setShowPanel(false);
  };

  if (!user) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          cursor: "pointer",
          transition: "all 0.2s ease",
          background: 'none',
          border: 'none',
          padding: 0
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.05)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        {/* Icono de campana */}
        <div style={{
          background: 'var(--color-border)',
          borderRadius: '8px',
          padding: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px'
        }}>
          {(() => {
            const BellIcon = FaBell as React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
            return <BellIcon size={16} style={{ color: 'var(--color-textSecondary)' }} />;
          })()}
        </div>
        
        {/* Badge de notificación superpuesto */}
        {unreadCount > 0 && (
          <div style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            backgroundColor: '#ff6b35', // Naranja vibrante
            color: 'white',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontFamily: theme.fonts.body,
            border: '2px solid white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </div>
        )}

      </button>

      <NotificationsPanel
        isVisible={showPanel}
        onClose={handleClose}
        onNotificationClick={handleNotificationClick}
      />
    </>
  );
};


const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Logo de marca: el MISMO ícono que la app/Play Store (tile verde + pelota).
  // Vive en client/public/favicon.png (también es el favicon del navegador).
  const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/favicon.png`;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isMobile } = useResponsive();
  const { logout, user } = useAuth();

  // Determinar si el usuario es owner (sin rol BOTH)
  const isOwner = user?.role === 'VENUE_OWNER';
  const flagsCtx = useFeatureFlags() as Record<string, any>;
  const venuesEnabled = Boolean(flagsCtx.venues);
  const rewardsEnabled = Boolean(flagsCtx.venue_rewards);
  const publicMatchDiscoverEnabled = Boolean(flagsCtx.PUBLIC_MATCH_DISCOVER);
  const { friendRequestCount, groupInvitationCount } = useSocialBadges();


  // Función para obtener menu items según el rol
  const getMenuItems = () => {
    const playerItems: { label: string; icon: IconType; path: string; badge?: number; divider?: boolean; sectionLabel?: string }[] = [
      { label: "HOME", icon: FaHome, path: "/home" },
      { label: "NUEVO PARTIDO", icon: FaPlusCircle, path: "/create-match" },
      ...(publicMatchDiscoverEnabled
        ? [
            { label: "BUSCAR PARTIDO", icon: FaSearch, path: "/discover" },
            { label: "MIS POSTULACIONES", icon: FaListUl, path: "/my-applications" },
          ]
        : []),
      { label: "MIS PARTIDOS", icon: FaListUl, path: "/matches" },
      { label: "PERFIL", icon: FaUserCircle, path: "/profile" },
      { label: "CONFIGURACIÓN", icon: FaCog, path: "/settings" },

      // Sección Social
      { label: "", icon: FaHome, path: "", divider: true, sectionLabel: "SOCIAL" },
      { label: "AMIGOS", icon: FaUserFriends, path: "/friends", badge: friendRequestCount || undefined },
      { label: "GRUPOS", icon: FaUsers, path: "/groups", badge: groupInvitationCount || undefined },

      // Sección de Canchas (solo si el módulo está habilitado)
      ...(venuesEnabled ? [
        { label: "", icon: FaHome, path: "", divider: true },
        { label: "BUSCAR CANCHAS", icon: MdSportsSoccer, path: "/venues" },
        { label: "MIS RESERVAS", icon: FaCalendarCheck, path: "/my-bookings" },
      ] : []),

      // Dueño que además juega (BOTH): ve el menú de jugador y, abajo, su cancha.
      ...(user?.role === 'BOTH' && (venuesEnabled || rewardsEnabled) ? [
        { label: "", icon: FaHome, path: "", divider: true, sectionLabel: "MI CANCHA" },
        ...(venuesEnabled ? [
          { label: "GRILLA RESERVAS", icon: FaCalendarCheck, path: "/owner/reservations" },
          { label: "MIS CANCHAS", icon: MdSportsSoccer, path: "/owner/venues" },
        ] : []),
        ...(rewardsEnabled ? [{ label: "PREMIOS", icon: FaListUl, path: "/canchas/premios" }] : []),
      ] : []),
    ];

    const ownerItems: { label: string; icon: IconType; path: string; badge?: number; divider?: boolean; sectionLabel?: string }[] = [
      { label: "GRILLA RESERVAS", icon: FaCalendarCheck, path: "/owner/reservations" },
      { label: "TURNOS FIJOS", icon: FaListUl, path: "/owner/fixed-bookings" },
      { label: "MIS CANCHAS", icon: MdSportsSoccer, path: "/owner/venues" },
      { label: "REPORTES Y CAJA", icon: FaDollarSign, path: "/owner/reports" },
      ...(rewardsEnabled ? [{ label: "PREMIOS", icon: FaListUl, path: "/canchas/premios" }] : []),
      { label: "CONFIGURACIÓN", icon: FaCog, path: "/owner/settings" },
    ];

    const adminItems: { label: string; icon: IconType; path: string; badge?: number; divider?: boolean; sectionLabel?: string }[] = [
      { label: "DASHBOARD", icon: MdDashboard, path: "/admin" },
      { label: "CLUBS", icon: MdBusiness, path: "/admin/clubs" },
    ];

    // Rol ADMIN puro: solo el panel. Un admin por la marca isAdmin (el caso de
    // producción: rol PLAYER) también juega, así que suma el panel a su menú.
    if (user?.role === 'ADMIN') return adminItems;
    if (isAdminUser(user)) {
      return [
        ...playerItems,
        { label: "", icon: FaHome, path: "", divider: true, sectionLabel: "ADMIN" },
        ...adminItems,
      ];
    }
    if (user?.role === 'VENUE_OWNER' && venuesEnabled) return ownerItems;
    return playerItems;
  };

  const menuItems = getMenuItems();



  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (notification.type === 'MATCH_FINISHED' && notification.matchId) {
      // Navegar al partido para dejar reviews
      navigate(`/match/${notification.matchId}`);
    } else if (notification.type === 'REVIEWS_RECEIVED_BATCH' && notification.matchId) {
      // Navegar al perfil para ver las reviews recibidas
      navigate('/profile');
    } else if (notification.type === 'REPORT_VALIDATED' && notification.matchId) {
      // Navegar al perfil para ver las estadísticas actualizadas
      navigate('/profile');
    } else if (notification.type === 'PLAYER_JOINED_MATCH' && notification.matchId) {
      // Navegar al partido para ver la lista de jugadores
      navigate(`/match/${notification.matchId}`);
    } else if (notification.type === 'PLAYER_LEFT_MATCH' && notification.matchId) {
      // Navegar al partido para ver la lista de jugadores
      navigate(`/match/${notification.matchId}`);
    } else if (notification.type === 'GROUP_ANNOUNCEMENT' && notification.groupId) {
      // Abrir el grupo y resaltar el anuncio (deep-link a la actividad).
      const q = notification.announcementId ? `?announcement=${notification.announcementId}` : '';
      navigate(`/groups/${notification.groupId}${q}`);
    } else if (notification.type === 'GROUP_POLL' && notification.groupId) {
      // Abrir el grupo directo en la pestaña Encuestas.
      navigate(`/groups/${notification.groupId}?tab=polls`);
    } else if (notification.type === 'GROUP_MESSAGE' && notification.groupId) {
      // Chat del grupo.
      navigate(`/groups/${notification.groupId}?tab=chat`);
    } else if (
      (notification.type === 'GROUP_MATCH_CREATED' || notification.type === 'GROUP_MATCH_SPOT_OPEN') &&
      notification.matchId
    ) {
      navigate(`/match/${notification.matchId}`);
    } else if (
      (notification.type === 'GROUP_JOIN_ACCEPTED' ||
        notification.type === 'GROUP_JOIN_REQUEST' ||
        notification.type === 'GROUP_MEMBER_LEFT') &&
      notification.groupId
    ) {
      navigate(`/groups/${notification.groupId}`);
    } else if (notification.type === 'GROUP_INVITATION') {
      navigate('/groups');
    }
    // Para otros tipos de notificación, no hacer nada por ahora
  };

  // Mobile hamburger menu
  if (isMobile) {
    return (
      <>
        {/* Mobile Header */}
        <header
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            background: theme.colors.surface,
            padding: "12px 16px",
            paddingTop: "calc(12px + env(safe-area-inset-top, 0px))",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <img src={LOGO_SRC} alt="Futbolero" width={32} height={32} style={{ borderRadius: "7px", display: "block" }} />
            <span style={{
              fontSize: "20px", 
              fontWeight: "bold", 
              color: theme.colors.primary,
              fontFamily: theme.fonts.heading 
            }}>
              FUTBOLERO
            </span>
          </div>
          
          {/* Información del usuario y notificaciones en mobile */}
          {user && (
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <button
                type="button"
                onClick={() => navigate('/profile')}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  padding: "4px 8px",
                  borderRadius: "6px",
                  transition: "all 0.2s ease",
                  background: 'none',
                  border: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <div style={{
                  width: "32px",
                  height: "32px",
                  background: theme.colors.primary,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  {user.avatar ? (
                    <img 
                      src={user.avatar} 
                      alt="Avatar" 
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        objectFit: "cover"
                      }}
                    />
                  ) : (
                    <div style={{ fontSize: '18px', color: theme.colors.white }}>👤</div>
                  )}
                </div>
                <span style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: theme.colors.text,
                  fontFamily: theme.fonts.body,
                  display: "none" // Oculto en mobile para ahorrar espacio
                }}>
                  {user.name || user.nickname}
                </span>
              </button>

              <NotificationButton onNotificationClick={handleNotificationClick} />
              
              <button
                onClick={toggleMobileMenu}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  color: theme.colors.text,
                  cursor: "pointer",
                  padding: "8px",
                  borderRadius: "4px",
                }}
              >
                ☰
              </button>
            </div>
          )}
        </header>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 999,
            }}
            onClick={() => setIsMobileMenuOpen(false)}
            onKeyDown={(e) => { if (e.key === 'Escape') setIsMobileMenuOpen(false); }}
            role="presentation"
          />
        )}

        {/* Mobile Menu */}
        <aside
          style={{
            position: "fixed",
            top: 0,
            left: isMobileMenuOpen ? 0 : "-280px",
            width: "280px",
            height: "100vh",
            background: theme.colors.surface,
            boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
            zIndex: 1001,
            transition: "left 0.3s ease",
            padding: "0",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header con Logo y Branding - Mobile */}
          <div style={{
            padding: "80px 24px 32px 24px",
            borderBottom: `1px solid ${theme.colors.border}`,
            marginBottom: "24px"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
              <img src={LOGO_SRC} alt="Futbolero" width={40} height={40} style={{ borderRadius: "9px", display: "block" }} />
              <div>
                <h1 style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: theme.colors.white,
                  fontFamily: theme.fonts.heading,
                  margin: 0,
                  letterSpacing: "0.5px"
                }}>
                  FUTBOLERO
                </h1>
                <p style={{
                  fontSize: "14px",
                  color: theme.colors.textSecondary,
                  margin: 0,
                  fontFamily: theme.fonts.body
                }}>
                </p>
              </div>
            </div>
          </div>

          {/* Navegación Principal - Mobile */}
          <nav style={{ flex: 1, padding: "0 16px" }}>
            {menuItems.map((item, index) => {
              // Render divider (with optional section label)
              if (item.divider) {
                return (
                  <div
                    key={`divider-${index}`}
                    style={{
                      margin: "12px 0",
                    }}
                  >
                    <div style={{ height: "1px", background: theme.colors.border, opacity: 0.3 }} />
                    {item.sectionLabel && (
                      <div style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color: theme.colors.textSecondary,
                        fontFamily: theme.fonts.heading,
                        letterSpacing: "1.5px",
                        padding: "8px 12px 0",
                        opacity: 0.6,
                      }}>
                        {item.sectionLabel}
                      </div>
                    )}
                  </div>
                );
              }

              const isActive = location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path));
              const Icon = item.icon as React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => handleMenuClick(item.path)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "16px 12px",
                    marginBottom: "4px",
                    cursor: "pointer",
                    color: isActive ? theme.colors.white : theme.colors.textSecondary,
                    background: isActive ? theme.colors.primary : "transparent",
                    borderRadius: "8px",
                    fontWeight: isActive ? 700 : 400,
                    fontFamily: theme.fonts.heading,
                    fontSize: "16px",
                    letterSpacing: "0.5px",
                    transition: "all 0.2s ease",
                    position: "relative",
                    border: 'none',
                    width: '100%',
                    textAlign: 'left'
                  }}
                >
                  <Icon size={20} style={{ marginRight: "16px", minWidth: "20px" }} />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.badge != null && item.badge > 0 && (
                    <div style={{
                      background: '#ff6b35',
                      color: theme.colors.white,
                      borderRadius: "12px",
                      padding: "2px 8px",
                      fontSize: "11px",
                      fontWeight: "bold",
                      minWidth: "18px",
                      textAlign: "center"
                    }}>
                      {item.badge}
                    </div>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Botón de Feedback - Mobile */}
          <div style={{ padding: "0 16px 16px 16px" }}>
            <FeedbackSidebarButton onClose={() => setIsMobileMenuOpen(false)} />
          </div>

                                 {/* En mobile no mostramos info del usuario aquí, está en el header */}

          {/* Botón de Logout - Mobile */}
          {user && (
            <div style={{
              padding: "16px 24px 24px 24px",
              borderTop: `1px solid ${theme.colors.border}`
            }}>
              <button
                type="button"
                onClick={handleLogout}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px",
                  cursor: "pointer",
                  color: "#FF6B6B",
                  background: "transparent",
                  borderRadius: "8px",
                  fontWeight: 400,
                  fontFamily: theme.fonts.heading,
                  fontSize: "16px",
                  transition: "all 0.2s ease",
                  border: 'none',
                  width: '100%',
                  textAlign: 'left'
                }}
              >
                {(() => {
                  const ArrowIcon = FaArrowRight as React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
                  return <ArrowIcon size={16} style={{ marginRight: "12px" }} />;
                })()}
                <span>CERRAR SESIÓN</span>
              </button>
            </div>
          )}
        </aside>
      </>
    );
  }

  // Desktop sidebar
  return (
    <aside
      style={{
        width: 220,
        background: theme.colors.surface,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        padding: "0",
        boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 100,
      }}
    >
      {/* Header con Logo — compacto */}
      <div style={{
        padding: "16px 20px",
        borderBottom: `1px solid ${theme.colors.border}`,
        marginBottom: "8px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <img src={LOGO_SRC} alt="Futbolero" width={32} height={32} style={{ borderRadius: "7px", display: "block", flexShrink: 0 }} />
          <h1 style={{
            fontSize: "18px",
            fontWeight: "bold",
            color: theme.colors.white,
            fontFamily: theme.fonts.heading,
            margin: 0,
            letterSpacing: "0.5px"
          }}>
            FUTBOLERO
          </h1>
        </div>
      </div>

      {/* Navegación Principal — compacta */}
      <nav style={{ padding: "0 12px", flex: 1 }}>
        {menuItems.map((item, index) => {
          // Render divider (with optional section label)
          if (item.divider) {
            return (
              <div
                key={`divider-desktop-${index}`}
                style={{ margin: "6px 0" }}
              >
                <div style={{ height: "1px", background: theme.colors.border, opacity: 0.3 }} />
                {item.sectionLabel && (
                  <div style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: theme.colors.textSecondary,
                    fontFamily: theme.fonts.heading,
                    letterSpacing: "1.5px",
                    padding: "5px 12px 0",
                    opacity: 0.6,
                  }}>
                    {item.sectionLabel}
                  </div>
                )}
              </div>
            );
          }

          const isActive = location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path));
          const Icon = item.icon as React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
          return (
            <button
              key={item.label}
              type="button"
              onClick={() => navigate(item.path)}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "9px 12px",
                marginBottom: "2px",
                cursor: "pointer",
                color: isActive ? theme.colors.white : theme.colors.textSecondary,
                background: isActive ? theme.colors.primary : "transparent",
                borderRadius: "8px",
                fontWeight: isActive ? 700 : 400,
                fontFamily: theme.fonts.heading,
                fontSize: "14px",
                letterSpacing: "0.5px",
                transition: "all 0.2s ease",
                position: "relative",
                border: 'none',
                width: '100%',
                textAlign: 'left'
              }}
            >
              <Icon size={18} style={{ marginRight: "12px", minWidth: "18px" }} />
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.badge && (
                <div style={{
                  background: theme.colors.primary,
                  color: theme.colors.white,
                  borderRadius: "12px",
                  padding: "2px 8px",
                  fontSize: "12px",
                  fontWeight: "bold",
                  minWidth: "20px",
                  textAlign: "center"
                }}>
                  {item.badge}
                </div>
              )}
            </button>
          );
        })}
      </nav>

      {/* Feedback compacto — solo PLAYER */}
      {user?.role !== 'VENUE_OWNER' && (
        <div style={{ padding: "0 12px 4px" }}>
          <FeedbackSidebarButton />
        </div>
      )}

      {/* Botón de Logout para Owners */}
      {user && user.role === 'VENUE_OWNER' && (
        <div style={{
          margin: "16px",
          marginTop: "auto",
        }}>
          <button
            type="button"
            onClick={handleLogout}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "12px 16px",
              cursor: "pointer",
              color: "#FF6B6B",
              background: "transparent",
              borderRadius: "8px",
              fontWeight: 600,
              fontFamily: theme.fonts.heading,
              fontSize: "14px",
              transition: "all 0.2s ease",
              border: `1px solid rgba(255, 107, 107, 0.3)`,
              width: '100%',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255, 107, 107, 0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            {(() => {
              const ArrowIcon = FaArrowRight as React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
              return <ArrowIcon size={14} style={{ marginRight: "10px" }} />;
            })()}
            <span>CERRAR SESIÓN</span>
          </button>
        </div>
      )}

      {/* Usuario compacto + Notificaciones — una fila (solo PLAYER) */}
      {user && user.role !== 'VENUE_OWNER' && (
        <div style={{
          margin: "0 12px 4px",
          padding: "10px 12px",
          background: "var(--color-card)",
          borderRadius: "10px",
          border: `1px solid ${theme.colors.border}`,
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}>
          <button
            type="button"
            onClick={() => navigate('/profile')}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              background: 'none',
              border: 'none',
              padding: 0,
              flex: 1,
              minWidth: 0,
            }}
          >
            <div style={{
              width: "32px",
              height: "32px",
              background: theme.colors.primary,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              overflow: "hidden",
            }}>
              {user.avatar ? (
                <img src={user.avatar} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
              ) : (
                <span style={{ fontSize: '16px', color: theme.colors.white }}>👤</span>
              )}
            </div>
            <span style={{
              fontSize: "13px",
              fontWeight: 600,
              color: theme.colors.white,
              fontFamily: theme.fonts.heading,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
              {user.name || user.nickname}
            </span>
          </button>
          <NotificationCounter onNotificationClick={handleNotificationClick} />
        </div>
      )}

      {/* Logout compacto */}
      {user && user.role !== 'VENUE_OWNER' && (
        <div style={{
          padding: "4px 12px 12px",
          borderTop: `1px solid ${theme.colors.border}`,
          marginTop: "4px",
        }}>
          <button
            type="button"
            onClick={handleLogout}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "8px 12px",
              cursor: "pointer",
              color: "#FF6B6B",
              background: "transparent",
              borderRadius: "8px",
              fontWeight: 400,
              fontFamily: theme.fonts.heading,
              fontSize: "13px",
              transition: "all 0.2s ease",
              border: 'none',
              width: '100%',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255, 107, 107, 0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            {(() => {
              const ArrowIcon = FaArrowRight as React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
              return <ArrowIcon size={13} style={{ marginRight: "10px" }} />;
            })()}
            <span>CERRAR SESION</span>
          </button>
        </div>
      )}
    </aside>
  );
};

export default Sidebar; 